import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/AuthContext";

// -----------------------------------------------------------------------------
// QENVA Landing — Iter88 rewrite matching the validated reference design.
// Strict palette from the reference:
//   Body bg    : #F4F6FA
//   Deep Navy  : #0B2341   (text primary + dark sections)
//   Navy soft  : #122d52   (dark cards)
//   Accent     : #22C55E   (green — CTA badges, success)
//   Electric   : #1677FF   (buttons, badges, KPI blue)
//   Muted body : #4A5B73
//   Footer bg  : #071527
// Fonts: Bodoni Moda (H1 hero + numbers), Playfair Display (H2 sections),
//        Inter (everything else, inherited).
// -----------------------------------------------------------------------------

const C = {
  bg: "#F4F6FA",
  navy: "#0B2341",
  navySoft: "#122d52",
  navyDeeper: "#0E2547",
  navyDeepest: "#071527",
  accent: "#22C55E",
  blue: "#1677FF",
  bodyMuted: "#4A5B73",
  eyebrow: "#22C55E",
  fbc: "#FBFCFE", // near-white on dark
};

// Reusable typography helpers
const FONT_BODONI = { fontFamily: "'Bodoni Moda', serif" };
const FONT_PLAYFAIR = { fontFamily: "'Playfair Display', serif" };

// -----------------------------------------------------------------------------
// SEO — set <title> & <meta name="description"> from the current locale.
// -----------------------------------------------------------------------------
function useSEO(title, description) {
  useEffect(() => {
    if (title) document.title = title;
    if (!description) return;
    let el = document.querySelector('meta[name="description"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "description");
      document.head.appendChild(el);
    }
    el.setAttribute("content", description);
    const setOG = (property, value) => {
      let og = document.querySelector(`meta[property="${property}"]`);
      if (!og) {
        og = document.createElement("meta");
        og.setAttribute("property", property);
        document.head.appendChild(og);
      }
      og.setAttribute("content", value);
    };
    setOG("og:title", title);
    setOG("og:description", description);
    setOG("og:type", "website");
  }, [title, description]);
}

// -----------------------------------------------------------------------------
// Language pill toggle (matches reference exactly: 2 pills inside a rounded bg).
// -----------------------------------------------------------------------------
function LangToggle() {
  const { i18n } = useTranslation();
  const current = (i18n.language || "fr").toLowerCase().startsWith("en") ? "EN" : "FR";
  const setLang = (loc) => i18n.changeLanguage(loc);
  const pill = (label, active) => (
    <button
      type="button"
      onClick={() => setLang(label.toLowerCase())}
      data-testid={`home-lang-${label.toLowerCase()}`}
      className="text-[12.5px] font-semibold px-2.5 py-[5px] rounded-md cursor-pointer transition-all"
      style={{
        background: active ? "#FFFFFF" : "transparent",
        color: active ? C.navy : "#7A8AA3",
        boxShadow: active ? "0 1px 2px rgba(11,35,65,0.12)" : "none",
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      className="inline-flex items-center rounded-lg p-[2px]"
      style={{ background: "#EDF1F7", border: "1px solid #E0E6EF" }}
    >
      {pill("FR", current === "FR")}
      {pill("EN", current === "EN")}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Features dropdown — 6 items linking to real app routes.
// -----------------------------------------------------------------------------
const FEATURES_MENU_ITEMS = [
  { key: "watch", to: "/alerts/register", tone: "blue-circle" },
  { key: "alerts", to: "/alerts", tone: "red-bar" },
  { key: "chat", to: "/chat", tone: "purple-bubble" },
  { key: "compare", to: "/comparisons", tone: "blue-bars" },
  { key: "reports", to: "/reports/executive", tone: "navy-doc" },
  { key: "vigilance", to: "/reports/vigilance", tone: "green-ring" },
];

function MenuIcon({ tone }) {
  // Small decorative glyph reproducing the reference. Not semantically meaningful.
  const base = "w-[34px] h-[34px] rounded-[9px] flex items-center justify-center shrink-0";
  if (tone === "blue-circle")
    return (
      <span className={base} style={{ background: "rgba(22,119,255,0.12)" }}>
        <span className="w-[11px] h-[11px] rounded-full" style={{ border: `2.5px solid ${C.blue}` }} />
      </span>
    );
  if (tone === "red-bar")
    return (
      <span className={base} style={{ background: "rgba(239,68,68,0.12)" }}>
        <span className="w-[6px] h-[11px] rounded-[3px]" style={{ background: "#EF4444" }} />
      </span>
    );
  if (tone === "purple-bubble")
    return (
      <span className={base} style={{ background: "rgba(168,85,247,0.12)" }}>
        <span className="w-[12px] h-[10px]" style={{ background: "#A855F7", borderRadius: "3px 3px 3px 0" }} />
      </span>
    );
  if (tone === "blue-bars")
    return (
      <span className={base} style={{ background: "rgba(22,119,255,0.12)" }}>
        <span className="flex gap-[2px]">
          <span className="w-[4px] h-[12px] rounded-[2px]" style={{ background: C.blue }} />
          <span className="w-[4px] h-[12px] rounded-[2px]" style={{ background: "rgba(22,119,255,0.4)" }} />
        </span>
      </span>
    );
  if (tone === "navy-doc")
    return (
      <span className={base} style={{ background: "rgba(11,35,65,0.09)" }}>
        <span className="w-[9px] h-[12px] rounded-[2px]" style={{ border: `2px solid ${C.navy}` }} />
      </span>
    );
  return (
    <span className={base} style={{ background: "rgba(34,197,94,0.13)" }}>
      <span
        className="w-[12px] h-[12px] rounded-full"
        style={{ border: `2.5px solid ${C.accent}`, borderRightColor: "transparent" }}
      />
    </span>
  );
}

function FeaturesDropdown({ open }) {
  const { t } = useTranslation("home");
  return (
    <div
      className={`absolute top-full left-[-24px] pt-[14px] z-50 transition-all duration-150 ${
        open ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-1"
      }`}
      data-testid="home-features-menu"
    >
      <div
        className="w-[660px] max-w-[92vw] p-[14px] grid grid-cols-1 sm:grid-cols-2 gap-1"
        style={{
          background: "#fff",
          border: "1px solid rgba(11,35,65,0.1)",
          borderRadius: 16,
          boxShadow: "0 28px 70px -24px rgba(11,35,65,0.32)",
        }}
      >
        {FEATURES_MENU_ITEMS.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            data-testid={`home-features-menu-${item.key}`}
            className="flex gap-[13px] px-[14px] py-[13px] rounded-[11px] no-underline transition-colors hover:bg-[#F4F7FC]"
          >
            <MenuIcon tone={item.tone} />
            <span>
              <span className="block text-[14px] font-semibold" style={{ color: C.navy }}>
                {t(`nav.features_menu.${item.key}.title`)}
              </span>
              <span className="block text-[12.5px] mt-[2px]" style={{ color: "#64748B", lineHeight: 1.45 }}>
                {t(`nav.features_menu.${item.key}.desc`)}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Nav — light-background top bar
// -----------------------------------------------------------------------------
function Nav() {
  const { t } = useTranslation("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef(null);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMenuOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenuOpen(false), 120);
  };

  return (
    <nav
      className="sticky top-0 z-40"
      style={{
        background: C.bg,
        borderBottom: "1px solid rgba(11,35,65,0.06)",
      }}
      data-testid="home-nav"
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 py-5 flex items-center">
        <Link to="/" className="flex items-center shrink-0" data-testid="home-logo-link" aria-label="QENVA · The Silent Guardian">
          <img
            src="/brand/v2-primary-logo.png"
            alt="QENVA · The Silent Guardian"
            draggable={false}
            className="h-[42px] w-auto block select-none"
            style={{ objectFit: "contain" }}
            data-testid="home-header-lockup"
          />
        </Link>

        {/* Desktop nav links */}
        <div
          className="hidden lg:flex items-center gap-7 ml-11 pr-7"
          style={{ fontSize: 15, fontWeight: 500, color: C.navy }}
        >
          <div className="relative" onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
            <span
              className="inline-flex items-center gap-1.5 cursor-pointer hover:text-[#1677FF] transition-colors"
              data-testid="home-nav-features"
            >
              {t("nav.features")}
              <span className="text-[9px] opacity-55 translate-y-[1px]">▼</span>
            </span>
            <FeaturesDropdown open={menuOpen} />
          </div>
          <a
            href="#securite"
            className="hover:text-[#1677FF] transition-colors"
            data-testid="home-nav-security"
          >
            {t("nav.security")}
          </a>
          <a
            href="#mission"
            className="hover:text-[#1677FF] transition-colors"
            data-testid="home-nav-mission"
          >
            {t("nav.mission")}
          </a>
          <Link
            to="/pricing"
            className="hover:text-[#1677FF] transition-colors"
            data-testid="home-nav-pricing"
          >
            {t("nav.pricing")}
          </Link>
        </div>

        {/* Right cluster: language + signin + demo */}
        <div className="ml-auto flex items-center gap-2 sm:gap-4 md:gap-[18px] shrink-0">
          <LangToggle />
          <Link
            to="/login"
            className="hidden sm:inline-flex whitespace-nowrap hover:text-[#1677FF] transition-colors"
            style={{ fontSize: 15, fontWeight: 500, color: C.navy }}
            data-testid="home-header-signin"
          >
            {t("nav.signin")}
          </Link>
          <Link
            to="/login?mode=signup"
            className="hidden sm:inline-flex items-center whitespace-nowrap rounded-lg text-white transition-colors"
            style={{
              fontSize: 14.5,
              fontWeight: 600,
              background: C.navy,
              padding: "10px 16px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#12294c")}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.navy)}
            data-testid="home-header-demo"
          >
            {t("nav.demo")}
          </Link>
          <button
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            data-testid="home-mobile-menu-toggle"
            style={{ color: C.navy }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-black/10 bg-white" data-testid="home-mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-3" style={{ color: C.navy, fontSize: 15, fontWeight: 500 }}>
            <Link to="/alerts/register" onClick={() => setMobileOpen(false)}>{t("nav.features")}</Link>
            <a href="#securite" onClick={() => setMobileOpen(false)}>{t("nav.security")}</a>
            <a href="#mission" onClick={() => setMobileOpen(false)}>{t("nav.mission")}</a>
            <Link to="/pricing" onClick={() => setMobileOpen(false)}>{t("nav.pricing")}</Link>
            <Link to="/login" onClick={() => setMobileOpen(false)}>{t("nav.signin")}</Link>
            <Link
              to="/login?mode=signup"
              onClick={() => setMobileOpen(false)}
              className="sm:hidden mt-2 inline-flex items-center justify-center rounded-lg text-white"
              style={{ background: C.navy, padding: "10px 16px", fontWeight: 600 }}
              data-testid="home-mobile-demo"
            >
              {t("nav.demo")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// -----------------------------------------------------------------------------
// Eyebrow (uppercase small caps)
// -----------------------------------------------------------------------------
function Eyebrow({ children, color = C.eyebrow, className = "", "data-testid": testId }) {
  return (
    <div
      className={`text-[12px] font-semibold uppercase ${className}`}
      style={{ letterSpacing: "0.18em", color }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

// Green dot with soft ring — used in status pills
function Dot({ color = C.accent, ring = false, size = 6 }) {
  return (
    <span
      className="inline-block rounded-full shrink-0"
      style={{
        width: size,
        height: size,
        background: color,
        boxShadow: ring ? `0 0 0 4px rgba(34,197,94,0.22)` : undefined,
      }}
    />
  );
}

// -----------------------------------------------------------------------------
// Hero — dark navy background, headline left + product widget right
// -----------------------------------------------------------------------------
function Hero() {
  const { t } = useTranslation("home");
  return (
    <header
      className="relative overflow-hidden"
      style={{ background: C.navy, color: "#EAF0FA" }}
      data-testid="home-hero"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1100px 520px at 78% -8%, rgba(22,119,255,0.22), transparent 60%), radial-gradient(700px 400px at 8% 110%, rgba(34,197,94,0.10), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1240px] mx-auto px-5 sm:px-10 pt-[78px] pb-[92px] grid gap-10 lg:gap-16 lg:grid-cols-[1.02fr,0.98fr] items-center">
        {/* Copy */}
        <div>
          <div className="inline-flex items-center gap-[9px] mb-[26px]" style={{ color: "#9FB4D6" }}>
            <Dot ring />
            <span className="text-[12px] font-semibold uppercase" style={{ letterSpacing: "0.18em" }}>
              {t("hero.eyebrow")}
            </span>
          </div>
          <h1
            className="m-0 mb-6"
            style={{
              ...FONT_BODONI,
              fontWeight: 600,
              fontSize: "clamp(40px, 6vw, 60px)",
              lineHeight: 1.04,
              letterSpacing: "-0.01em",
              color: C.fbc,
            }}
            data-testid="home-hero-title"
          >
            {t("hero.title_l1")}
            <br />
            {t("hero.title_l2")}
            <br />
            {t("hero.title_l3")}
          </h1>
          <p
            className="max-w-[468px] m-0 mb-9"
            style={{ fontSize: 18, lineHeight: 1.62, color: "#B7C6DE", fontWeight: 400 }}
          >
            {t("hero.subtitle")}
          </p>
          <div className="flex items-center gap-4 mb-[34px] flex-wrap">
            <Link
              to="/login?mode=signup"
              className="inline-flex items-center rounded-[9px] text-white transition-all hover:brightness-110"
              style={{
                fontSize: 15.5,
                fontWeight: 600,
                background: C.blue,
                padding: "15px 26px",
                boxShadow: "0 12px 30px -12px rgba(22,119,255,0.7)",
              }}
              data-testid="home-hero-cta-primary"
            >
              {t("hero.cta_primary")}
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center rounded-[9px] transition-colors"
              style={{
                fontSize: 15.5,
                fontWeight: 500,
                color: "#EAF0FA",
                border: "1px solid rgba(234,240,250,0.28)",
                padding: "15px 24px",
                background: "transparent",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(234,240,250,0.6)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(234,240,250,0.28)")}
              data-testid="home-hero-cta-secondary"
            >
              {t("hero.cta_secondary")} →
            </Link>
          </div>
          <div className="flex items-center gap-3.5 text-[13.5px]" style={{ color: "#8FA4C6" }}>
            <span className="inline-flex items-center gap-[7px]">
              <span style={{ color: C.accent, fontSize: 15 }}>✓</span> {t("hero.trust_1")}
            </span>
            <span className="w-[3px] h-[3px] rounded-full" style={{ background: "#4C6088" }} />
            <span className="inline-flex items-center gap-[7px]">
              <span style={{ color: C.accent, fontSize: 15 }}>✓</span> {t("hero.trust_2")}
            </span>
          </div>
        </div>

        {/* Product widget */}
        <div className="relative" style={{ perspective: 1400 }}>
          <div
            className="absolute rounded-[28px]"
            style={{
              inset: -24,
              background: "linear-gradient(135deg, rgba(22,119,255,0.28), rgba(34,197,94,0.14))",
              filter: "blur(34px)",
              opacity: 0.7,
            }}
          />
          <div
            className="absolute rounded-[18px] hidden md:block"
            style={{
              left: 32,
              right: 32,
              top: 24,
              bottom: -24,
              background: C.navySoft,
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              transform: "rotateY(-8deg) translateZ(-40px)",
            }}
          />
          <div
            className="relative rounded-[18px] overflow-hidden"
            style={{
              background: C.navySoft,
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.45), 0 4px 20px rgba(0,0,0,0.2)",
              transformStyle: "preserve-3d",
              transform: "rotateY(-5deg) rotateX(2deg)",
            }}
            data-testid="home-hero-widget"
          >
            <div className="p-[26px] pb-[28px]">
              <div className="flex items-start justify-between">
                <div>
                  <div
                    className="text-[10px] font-semibold uppercase"
                    style={{ letterSpacing: "0.2em", color: "rgba(245,247,250,0.6)" }}
                  >
                    {t("hero.widget.overline")}
                  </div>
                  <div
                    className="mt-[5px]"
                    style={{
                      ...FONT_BODONI,
                      fontSize: 20,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      color: "#F5F7FA",
                    }}
                  >
                    {t("hero.widget.brand")}
                  </div>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full"
                  style={{
                    color: "#86EFAC",
                    background: "rgba(34,197,94,0.15)",
                    padding: "5px 11px",
                    boxShadow: "0 0 14px rgba(34,197,94,0.3)",
                  }}
                >
                  <Dot />
                  {t("hero.widget.badge")}
                </span>
              </div>
              {/* Stat tiles */}
              <div className="grid grid-cols-3 gap-2.5 mt-6">
                {[
                  { label: t("hero.widget.documents"), value: "42", tint: "rgba(22,119,255,0.14)", color: "#93C5FD" },
                  { label: t("hero.widget.alerts"), value: "7", tint: "rgba(239,68,68,0.14)", color: "#FCA5A5" },
                  { label: t("hero.widget.decisions"), value: "3", tint: "rgba(168,85,247,0.14)", color: "#D8B4FE" },
                ].map((tile) => (
                  <div
                    key={tile.label}
                    className="rounded-xl px-[15px] py-[13px]"
                    style={{ background: tile.tint, border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <div
                      className="text-[9px] font-semibold uppercase"
                      style={{ letterSpacing: "0.16em", color: "rgba(245,247,250,0.6)" }}
                    >
                      {tile.label}
                    </div>
                    <div className="mt-[5px]" style={{ ...FONT_BODONI, fontSize: 26, fontWeight: 600, color: tile.color }}>
                      {tile.value}
                    </div>
                  </div>
                ))}
              </div>
              {/* Alert rows */}
              <div className="mt-6 flex flex-col gap-[15px]">
                {[
                  { color: "#EF4444", text: t("hero.widget.alert_urgent") },
                  { color: "#F59E0B", text: t("hero.widget.alert_warning") },
                  { color: C.accent, text: t("hero.widget.alert_ok") },
                ].map((row) => (
                  <div key={row.text} className="flex items-center gap-[11px] text-[13.5px]" style={{ color: "rgba(245,247,250,0.74)" }}>
                    <span
                      className="w-[7px] h-[7px] rounded-full shrink-0"
                      style={{ background: row.color, boxShadow: `0 0 8px ${row.color}` }}
                    />
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis">{row.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

// -----------------------------------------------------------------------------
// Problem — light bg, 3 pillar cards
// -----------------------------------------------------------------------------
function Problem() {
  const { t } = useTranslation("home");
  const cards = [
    { key: "scattered", top: C.navy, titleColor: C.navy },
    { key: "silent", top: C.navy, titleColor: C.navy },
    { key: "watched", top: C.accent, titleColor: C.accent },
  ];
  return (
    <section className="max-w-[1240px] mx-auto px-5 sm:px-10 pt-[104px] pb-[88px]" data-testid="home-problem">
      <div className="max-w-[820px]">
        <Eyebrow className="mb-[22px]" data-testid="home-problem-eyebrow">
          {t("problem.eyebrow")}
        </Eyebrow>
        <h2
          className="m-0 mb-6"
          style={{
            ...FONT_PLAYFAIR,
            fontWeight: 600,
            fontSize: "clamp(30px, 4.5vw, 42px)",
            lineHeight: 1.14,
            letterSpacing: "-0.01em",
            color: C.navy,
          }}
        >
          {t("problem.title_l1")}<br />{t("problem.title_l2")}
        </h2>
        <p className="max-w-[680px] m-0" style={{ fontSize: 18, lineHeight: 1.66, color: C.bodyMuted }}>
          {t("problem.body")}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-7 mt-[60px]">
        {cards.map((c) => (
          <div key={c.key} className="pt-[22px]" style={{ borderTop: `2px solid ${c.top}` }}>
            <div style={{ ...FONT_PLAYFAIR, fontSize: 26, fontWeight: 600, color: c.titleColor, lineHeight: 1.1 }}>
              {t(`problem.card_${c.key}_title`)}
            </div>
            <div className="mt-3.5 text-[15.5px]" style={{ lineHeight: 1.55, color: C.bodyMuted }}>
              {t(`problem.card_${c.key}_body`)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// How it works — dark, 3 numbered cards
// -----------------------------------------------------------------------------
function HowItWorks() {
  const { t } = useTranslation("home");
  const steps = ["s1", "s2", "s3"];
  return (
    <section id="fonctionnalites" style={{ background: C.navy, color: "#EAF0FA" }} data-testid="home-how">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pt-[96px] pb-[100px]">
        <div className="flex items-end justify-between gap-10 mb-[58px] flex-wrap">
          <div className="max-w-[640px]">
            <Eyebrow className="mb-5">{t("how.eyebrow")}</Eyebrow>
            <h2
              className="m-0"
              style={{
                ...FONT_PLAYFAIR,
                fontWeight: 600,
                fontSize: "clamp(28px, 4vw, 40px)",
                lineHeight: 1.16,
                color: C.fbc,
              }}
            >
              {t("how.title")}
            </h2>
          </div>
          <p className="m-0 max-w-[330px] text-[16px]" style={{ lineHeight: 1.6, color: "#9FB4D6" }}>
            {t("how.subtitle")}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[26px]">
          {steps.map((s, i) => (
            <div
              key={s}
              className="rounded-[14px] px-[30px] py-[34px]"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)" }}
            >
              <div className="flex items-center justify-between mb-[26px]">
                <span
                  style={{
                    ...FONT_BODONI,
                    fontSize: 15,
                    fontWeight: 600,
                    color: C.accent,
                    letterSpacing: "0.1em",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="text-[11px] font-semibold uppercase"
                  style={{ letterSpacing: "0.14em", color: "#6E85AC" }}
                >
                  {t(`how.${s}_label`)}
                </span>
              </div>
              <h3
                className="m-0 mb-3"
                style={{ ...FONT_PLAYFAIR, fontSize: 23, fontWeight: 600, color: C.fbc }}
              >
                {t(`how.${s}_title`)}
              </h3>
              <p className="m-0 text-[15px]" style={{ lineHeight: 1.6, color: "#A9BBD8" }}>
                {t(`how.${s}_body`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Product deep-dive — 2 alternating rows with striped placeholders.
// (Placeholders are intentional per user: real screenshots coming later.)
// -----------------------------------------------------------------------------
const STRIPE_STYLE = {
  background:
    "repeating-linear-gradient(135deg, #EEF2F8, #EEF2F8 11px, #E7EDF6 11px, #E7EDF6 22px)",
  border: "1px solid #DEE5EF",
  borderRadius: 14,
  aspectRatio: "16 / 11",
};
function Placeholder({ label, testId }) {
  return (
    <div className="flex items-center justify-center" style={STRIPE_STYLE} data-testid={testId}>
      <span
        className="rounded-md px-3.5 py-2 text-[12.5px]"
        style={{
          fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
          letterSpacing: "0.06em",
          color: "#8494AC",
          background: "#F4F6FA",
          border: "1px solid #DEE5EF",
        }}
      >
        {label}
      </span>
    </div>
  );
}
function ProductRow({ side, eyebrow, title, body, features, placeholder, testId, placeholderTestId }) {
  const copy = (
    <div>
      <Eyebrow className="mb-5">{eyebrow}</Eyebrow>
      <h2
        className="m-0 mb-[18px]"
        style={{
          ...FONT_PLAYFAIR,
          fontWeight: 600,
          fontSize: "clamp(26px, 3.4vw, 34px)",
          lineHeight: 1.2,
          color: C.navy,
        }}
      >
        {title}
      </h2>
      <p className="m-0 mb-[26px]" style={{ fontSize: 16.5, lineHeight: 1.64, color: C.bodyMuted }}>
        {body}
      </p>
      <div className="flex flex-col gap-3.5">
        {features.map((f) => (
          <div key={f} className="flex items-center gap-3 text-[15.5px] font-medium" style={{ color: C.navy }}>
            <span style={{ color: C.accent, fontSize: 17 }}>✓</span>
            {f}
          </div>
        ))}
      </div>
    </div>
  );
  const preview = <Placeholder label={placeholder} testId={placeholderTestId} />;
  return (
    <div
      className={`grid gap-10 lg:gap-16 items-center ${
        side === "left" ? "lg:grid-cols-[0.92fr,1.08fr]" : "lg:grid-cols-[1.08fr,0.92fr]"
      }`}
      data-testid={testId}
    >
      {side === "left" ? (
        <>
          {copy}
          {preview}
        </>
      ) : (
        <>
          {preview}
          {copy}
        </>
      )}
    </div>
  );
}
function ProductDeepDive() {
  const { t } = useTranslation("home");
  return (
    <section className="max-w-[1240px] mx-auto px-5 sm:px-10 pt-[100px] pb-10" data-testid="home-product">
      <div className="mb-[96px]">
        <ProductRow
          side="left"
          testId="home-product-vault"
          placeholderTestId="home-product-vault-placeholder"
          eyebrow={t("product.vault_eyebrow")}
          title={t("product.vault_title")}
          body={t("product.vault_body")}
          features={[t("product.vault_f1"), t("product.vault_f2"), t("product.vault_f3")]}
          placeholder={t("product.vault_placeholder")}
        />
      </div>
      <ProductRow
        side="right"
        testId="home-product-calendar"
        placeholderTestId="home-product-calendar-placeholder"
        eyebrow={t("product.cal_eyebrow")}
        title={t("product.cal_title")}
        body={t("product.cal_body")}
        features={[t("product.cal_f1"), t("product.cal_f2"), t("product.cal_f3")]}
        placeholder={t("product.cal_placeholder")}
      />
    </section>
  );
}

// -----------------------------------------------------------------------------
// Demo section — dark bg, fake app window (illustrative placeholder for
// the real video that will replace it later).
// -----------------------------------------------------------------------------
function DemoWindow() {
  const { t } = useTranslation("home");
  const rows = [
    { key: "row1", tone: "danger" },
    { key: "row2", tone: "warning" },
    { key: "row3", tone: "info" },
    { key: "row4", tone: "success" },
  ];
  const tone = {
    danger: { color: "#FCA5A5", bg: "rgba(239,68,68,0.16)" },
    warning: { color: "#F6BE4F", bg: "rgba(245,158,11,0.16)" },
    info: { color: "#93C5FD", bg: "rgba(22,119,255,0.16)" },
    success: { color: "#86EFAC", bg: "rgba(34,197,94,0.16)" },
  };
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: C.navyDeeper,
        border: "1px solid rgba(255,255,255,0.09)",
        boxShadow: "0 40px 100px -30px rgba(0,0,0,0.7)",
      }}
      data-testid="home-demo-window"
    >
      {/* window chrome */}
      <div
        className="flex items-center gap-3.5 px-[18px] py-[14px]"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}
      >
        <div className="flex gap-[7px]">
          <span className="w-[11px] h-[11px] rounded-full" style={{ background: "#EF5F56" }} />
          <span className="w-[11px] h-[11px] rounded-full" style={{ background: "#F6BE4F" }} />
          <span className="w-[11px] h-[11px] rounded-full" style={{ background: "#61C554" }} />
        </div>
        <div className="flex-1 flex justify-center">
          <div
            className="text-[12px] rounded-md px-4 py-[5px]"
            style={{
              color: "rgba(245,247,250,0.5)",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.06)",
              fontFamily: "ui-monospace, Menlo, monospace",
            }}
          >
            {t("demo.window_url")}
          </div>
        </div>
      </div>

      {/* body */}
      <div className="grid grid-cols-1 md:grid-cols-[216px,1fr]">
        {/* sidebar */}
        <div
          className="hidden md:flex flex-col gap-[5px] px-4 py-[22px]"
          style={{ borderRight: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.015)" }}
        >
          <div className="flex items-center gap-[9px] px-2 pb-[18px]">
            <span className="w-[9px] h-[9px] rounded-full" style={{ background: C.accent }} />
            <span style={{ ...FONT_BODONI, fontSize: 18, fontWeight: 600, letterSpacing: "0.14em", color: "#F5F7FA" }}>
              {t("hero.widget.brand")}
            </span>
          </div>
          {[
            { key: "sb_dashboard", active: true },
            { key: "sb_documents" },
            { key: "sb_deadlines" },
            { key: "sb_alerts" },
            { key: "sb_reports" },
          ].map((it) => (
            <div
              key={it.key}
              className="flex items-center gap-[11px] px-3 py-2.5 rounded-[9px] text-[13.5px]"
              style={{
                background: it.active ? "rgba(22,119,255,0.16)" : "transparent",
                border: it.active ? "1px solid rgba(22,119,255,0.28)" : "1px solid transparent",
                color: it.active ? "#EAF0FA" : "rgba(245,247,250,0.6)",
                fontWeight: it.active ? 600 : 500,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-sm"
                style={{ background: it.active ? C.blue : "rgba(245,247,250,0.3)" }}
              />
              {t(`demo.${it.key}`)}
            </div>
          ))}
        </div>

        {/* main panel */}
        <div className="px-[28px] pt-[26px] pb-[30px]">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <div style={{ ...FONT_PLAYFAIR, fontSize: 22, fontWeight: 600, color: "#F5F7FA" }}>
                {t("demo.panel_title")}
              </div>
              <div className="text-[12.5px] mt-0.5" style={{ color: "rgba(245,247,250,0.5)" }}>
                {t("demo.panel_subtitle")}
              </div>
            </div>
            <div
              className="inline-flex items-center gap-2 text-[12.5px] font-semibold rounded-full px-[13px] py-[7px]"
              style={{
                color: "#86EFAC",
                background: "rgba(34,197,94,0.14)",
                border: "1px solid rgba(34,197,94,0.3)",
              }}
            >
              <Dot />
              {t("demo.panel_badge")}
            </div>
          </div>

          {/* score + kpis */}
          <div className="grid gap-4 mb-4 md:grid-cols-[1.15fr,1fr]">
            {/* Guardian Score card */}
            <div
              className="rounded-[14px] px-[26px] py-6 flex items-center gap-[26px] flex-wrap"
              style={{
                background: "linear-gradient(150deg, rgba(22,119,255,0.12), rgba(255,255,255,0.02))",
                border: "1px solid rgba(255,255,255,0.09)",
              }}
            >
              <div
                className="relative w-[150px] h-[150px] rounded-full shrink-0"
                style={{
                  background:
                    "conic-gradient(#1677FF 0deg 216deg, #22C55E 216deg 280.8deg, rgba(255,255,255,0.08) 280.8deg 360deg)",
                }}
              >
                <div
                  className="absolute inset-[13px] rounded-full flex flex-col items-center justify-center"
                  style={{ background: "#102a4f" }}
                >
                  <div
                    className="text-[9px] font-semibold uppercase"
                    style={{ letterSpacing: "0.14em", color: "rgba(245,247,250,0.45)", lineHeight: 1 }}
                  >
                    {t("demo.score_eyebrow")}
                  </div>
                  <div
                    style={{ ...FONT_BODONI, fontSize: 44, fontWeight: 600, color: "#F5F7FA", lineHeight: 1 }}
                    className="mt-0.5"
                  >
                    {t("demo.score_value")}
                  </div>
                  <div
                    className="text-[10px] uppercase mt-1"
                    style={{ letterSpacing: "0.18em", color: "rgba(245,247,250,0.5)" }}
                  >
                    {t("demo.score_total")}
                  </div>
                </div>
              </div>
              <div>
                <div
                  className="text-[11px] font-semibold uppercase"
                  style={{ letterSpacing: "0.16em", color: C.accent }}
                >
                  {t("demo.score_label")}
                </div>
                <div
                  className="mt-1.5"
                  style={{ ...FONT_PLAYFAIR, fontSize: 19, fontWeight: 600, color: "#F5F7FA", lineHeight: 1.3 }}
                >
                  {t("demo.score_sub_l1")}<br />{t("demo.score_sub_l2")}
                </div>
                <div
                  className="inline-flex items-center gap-1.5 mt-3.5 rounded-full px-3 py-[5px] text-[12px] font-semibold"
                  style={{
                    color: "rgba(245,247,250,0.55)",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {t("demo.score_pill")}
                </div>
              </div>
            </div>
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: "kpi_alerts", value: "7", color: "#FCA5A5" },
                { key: "kpi_next", value: "3", color: "#93C5FD" },
                { key: "kpi_tracked", value: "42", color: "#F5F7FA" },
                { key: "kpi_decisions", value: "2", color: C.accent },
              ].map((k) => (
                <div
                  key={k.key}
                  className="rounded-[14px] px-5 py-[18px] flex flex-col justify-center"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div style={{ ...FONT_BODONI, fontSize: 30, fontWeight: 600, color: k.color }}>{k.value}</div>
                  <div className="text-[12.5px] mt-0.5" style={{ color: "rgba(245,247,250,0.6)" }}>
                    {t(`demo.${k.key}`)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div
            className="rounded-[14px] overflow-hidden"
            style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <div
              className="hidden md:grid gap-3 px-5 py-3 text-[10.5px] font-semibold uppercase"
              style={{
                gridTemplateColumns: "2.4fr 1fr 1.3fr 1fr",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                letterSpacing: "0.12em",
                color: "rgba(245,247,250,0.45)",
              }}
            >
              <span>{t("demo.th_doc")}</span>
              <span>{t("demo.th_type")}</span>
              <span>{t("demo.th_deadline")}</span>
              <span>{t("demo.th_status")}</span>
            </div>
            {rows.map((r, idx) => (
              <div
                key={r.key}
                className="grid gap-3 px-5 py-[15px] items-center text-[13.5px]"
                style={{
                  gridTemplateColumns: "2.4fr 1fr 1.3fr 1fr",
                  borderBottom: idx === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.05)",
                  color: "#EAF0FA",
                }}
              >
                <span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                  {t(`demo.${r.key}_doc`)}
                </span>
                <span style={{ color: "rgba(245,247,250,0.6)" }}>{t(`demo.${r.key}_type`)}</span>
                <span style={{ color: "rgba(245,247,250,0.6)" }}>{t(`demo.${r.key}_deadline`)}</span>
                <span>
                  <span
                    className="text-[11.5px] font-bold rounded-full px-2.5 py-1 whitespace-nowrap"
                    style={{ color: tone[r.tone].color, background: tone[r.tone].bg }}
                  >
                    {t(`demo.${r.key}_status`)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
function Demo() {
  const { t } = useTranslation("home");
  return (
    <section className="relative overflow-hidden" style={{ background: C.navy }} data-testid="home-demo">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1000px 460px at 82% -6%, rgba(22,119,255,0.20), transparent 60%), radial-gradient(700px 400px at 4% 108%, rgba(34,197,94,0.10), transparent 58%)",
        }}
      />
      <div className="relative max-w-[1240px] mx-auto px-5 sm:px-10 pt-[100px] pb-[104px]">
        <div className="max-w-[680px] mb-[52px]">
          <Eyebrow className="mb-5">{t("demo.eyebrow")}</Eyebrow>
          <h2
            className="m-0 mb-[18px]"
            style={{
              ...FONT_PLAYFAIR,
              fontWeight: 600,
              fontSize: "clamp(28px, 4vw, 40px)",
              lineHeight: 1.15,
              color: C.fbc,
            }}
          >
            {t("demo.title")}
          </h2>
          <p className="m-0 text-[17px]" style={{ lineHeight: 1.6, color: "#A9BBD8" }}>
            {t("demo.body")}
          </p>
        </div>
        <DemoWindow />
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Security — inner dark card on light bg
// -----------------------------------------------------------------------------
function Security() {
  const { t } = useTranslation("home");
  return (
    <section id="securite" className="max-w-[1240px] mx-auto px-5 sm:px-10 py-[84px]" data-testid="home-security">
      <div
        className="rounded-[20px] grid gap-12 items-center p-8 sm:p-14 lg:grid-cols-[1fr,auto]"
        style={{ background: C.navy }}
      >
        <div>
          <Eyebrow className="mb-[18px]">{t("security.eyebrow")}</Eyebrow>
          <h2
            className="m-0 mb-3.5"
            style={{
              ...FONT_PLAYFAIR,
              fontWeight: 600,
              fontSize: "clamp(24px, 3.2vw, 32px)",
              lineHeight: 1.2,
              color: C.fbc,
            }}
          >
            {t("security.title")}
          </h2>
          <p className="m-0 text-[16px]" style={{ lineHeight: 1.62, color: "#A9BBD8", maxWidth: 520 }}>
            {t("security.body")}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          {["badge_1", "badge_2", "badge_3"].map((b) => (
            <div
              key={b}
              className="rounded-[11px] px-[18px] py-4 text-center text-[13px] font-semibold"
              style={{ border: "1px solid rgba(255,255,255,0.14)", color: "#EAF0FA", minWidth: 150 }}
            >
              {t(`security.${b}`)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Mission
// -----------------------------------------------------------------------------
function Mission() {
  const { t } = useTranslation("home");
  const pillars = ["vigilance", "protection", "trust"];
  return (
    <section
      id="mission"
      className="relative overflow-hidden"
      style={{ background: C.navy, color: "#EAF0FA" }}
      data-testid="home-mission"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 460px at 12% -10%, rgba(22,119,255,0.16), transparent 60%), radial-gradient(700px 420px at 96% 108%, rgba(34,197,94,0.10), transparent 58%)",
        }}
      />
      <div className="relative max-w-[1240px] mx-auto px-5 sm:px-10 pt-[104px] pb-[96px]">
        <div className="inline-flex items-center gap-[9px] mb-7" style={{ color: C.accent }}>
          <Dot ring />
          <span className="text-[12px] font-semibold uppercase" style={{ letterSpacing: "0.18em" }}>
            {t("mission.eyebrow")}
          </span>
        </div>
        <h2
          className="m-0 mb-8"
          style={{
            ...FONT_PLAYFAIR,
            fontWeight: 600,
            fontSize: "clamp(28px, 4.5vw, 44px)",
            lineHeight: 1.18,
            letterSpacing: "-0.005em",
            color: C.fbc,
          }}
        >
          {t("mission.title_l1")}<br />{t("mission.title_l2")}
        </h2>
        <div className="flex flex-col gap-[22px] text-[18px]" style={{ lineHeight: 1.68, color: "#B7C6DE" }}>
          <p className="m-0">
            {t("mission.p1_before")}
            <span style={{ color: "#F5F7FA", fontWeight: 600 }}>{t("mission.p1_bold")}</span>
            {t("mission.p1_after")}
          </p>
          <p className="m-0">{t("mission.p2")}</p>
          <p className="m-0">
            {t("mission.p3_before")}
            <span style={{ ...FONT_BODONI, fontStyle: "italic", color: "#F5F7FA" }}>{t("mission.p3_italic")}</span>
            {t("mission.p3_after")}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-[56px]">
          {pillars.map((p) => (
            <div key={p} className="pt-[22px]" style={{ borderTop: `1px solid ${C.accent}` }}>
              <div style={{ ...FONT_PLAYFAIR, fontSize: 20, fontWeight: 600, color: C.fbc, marginBottom: 9 }}>
                {t(`mission.pillar_${p}_title`)}
              </div>
              <div className="text-[14.5px]" style={{ lineHeight: 1.55, color: "#9FB4D6" }}>
                {t(`mission.pillar_${p}_body`)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Final CTA — centred, dark
// -----------------------------------------------------------------------------
function FinalCTA() {
  const { t } = useTranslation("home");
  return (
    <section className="relative overflow-hidden" style={{ background: C.navy }} data-testid="home-final-cta">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(800px 380px at 50% 120%, rgba(22,119,255,0.24), transparent 62%)",
        }}
      />
      <div className="relative max-w-[900px] mx-auto px-5 sm:px-10 py-[92px] text-center">
        <h2
          className="m-0 mb-5"
          style={{
            ...FONT_BODONI,
            fontWeight: 600,
            fontSize: "clamp(30px, 5vw, 46px)",
            lineHeight: 1.1,
            color: C.fbc,
          }}
        >
          {t("cta_final.title_l1")}<br />{t("cta_final.title_l2")}
        </h2>
        <p className="max-w-[560px] mx-auto mb-[34px] text-[18px]" style={{ lineHeight: 1.6, color: "#A9BBD8" }}>
          {t("cta_final.body")}
        </p>
        <div className="flex items-center justify-center gap-4">
          <Link
            to="/login?mode=signup"
            className="inline-flex items-center rounded-[9px] text-white transition-all hover:brightness-110"
            style={{
              fontSize: 16,
              fontWeight: 600,
              background: C.blue,
              padding: "16px 30px",
              boxShadow: "0 14px 34px -12px rgba(22,119,255,0.75)",
            }}
            data-testid="home-final-cta-button"
          >
            {t("cta_final.button")}
          </Link>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Footer — very dark background
// -----------------------------------------------------------------------------
function Footer() {
  const { t } = useTranslation("home");
  return (
    <footer style={{ background: C.navyDeepest, color: "#8FA4C6" }} data-testid="home-footer">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pt-[52px] pb-10 grid gap-10 lg:grid-cols-[1.4fr,1fr,1fr,1fr]">
        <div>
          <img
            src="/brand/v2-dark-lockup.png"
            alt="QENVA · The Silent Guardian"
            draggable={false}
            className="block h-16 w-auto select-none"
            style={{
              margin: "-6px 0 14px -4px",
              objectFit: "contain",
              border: "none",
              outline: "none",
              background: "transparent",
              boxShadow: "none",
            }}
            data-testid="home-footer-lockup"
          />
          <p className="m-0 text-[14px] max-w-[280px]" style={{ lineHeight: 1.6, color: "#7089AE" }}>
            {t("footer.tagline")}
          </p>
        </div>
        {[
          {
            title: "col_product_title",
            items: [
              { key: "col_product_features", to: "/alerts/register" },
              { key: "col_product_security", to: "#securite" },
              { key: "col_product_pricing", to: "/pricing" },
              { key: "col_product_integrations", to: "/pricing" },
            ],
          },
          {
            title: "col_company_title",
            items: [
              { key: "col_company_about", to: "#mission" },
              { key: "col_company_customers", to: "/pricing" },
              { key: "col_company_contact", to: "mailto:contact@qenva.com" },
            ],
          },
          {
            title: "col_legal_title",
            items: [
              { key: "col_legal_privacy", to: "/legal/privacy" },
              { key: "col_legal_terms", to: "/legal/terms" },
            ],
          },
        ].map((col) => (
          <div key={col.title}>
            <div
              className="text-[12px] font-semibold uppercase mb-4"
              style={{ letterSpacing: "0.12em", color: "#5E769B" }}
            >
              {t(`footer.${col.title}`)}
            </div>
            <div className="flex flex-col gap-[11px] text-[14px]">
              {col.items.map((it) => {
                const isHash = it.to.startsWith("#");
                const isMail = it.to.startsWith("mailto:");
                const cls = "cursor-pointer transition-colors hover:text-[#EAF0FA]";
                if (isHash || isMail) {
                  return (
                    <a key={it.key} href={it.to} className={cls}>
                      {t(`footer.${it.key}`)}
                    </a>
                  );
                }
                return (
                  <Link key={it.key} to={it.to} className={cls}>
                    {t(`footer.${it.key}`)}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div
          className="max-w-[1240px] mx-auto px-5 sm:px-10 py-5 flex items-center justify-between text-[13px] gap-3 flex-wrap"
          style={{ color: "#5E769B" }}
        >
          <span>{t("footer.copyright")}</span>
          <span>{t("footer.hosting")}</span>
        </div>
      </div>
    </footer>
  );
}

// -----------------------------------------------------------------------------
// Page composition
// -----------------------------------------------------------------------------
export default function Home() {
  const { user } = useAuth();
  const { t } = useTranslation("home");
  useSEO(t("meta.title"), t("meta.description"));
  if (user) return <Navigate to="/dashboard" replace />;
  return (
    <div style={{ background: C.bg, color: C.navy, fontFamily: "'Inter', system-ui, sans-serif", overflowX: "hidden" }}>
      <Nav />
      <Hero />
      <Problem />
      <HowItWorks />
      <ProductDeepDive />
      <Demo />
      <Security />
      <Mission />
      <FinalCTA />
      <Footer />
    </div>
  );
}
